import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import type { AnyRecord, AppData, DrawerState } from "../../lib/types";
import { ChannelSettings } from "../settings/SettingsPage";

type IntegrationsPageProps = {
  data: AppData;
  drawer: DrawerState;
  setDrawer: (drawer: DrawerState) => void;
};

export function IntegrationsPage({ data, drawer, setDrawer }: IntegrationsPageProps) {
  const [params, setParams] = useSearchParams();
  const activeView = params.get("view") || "channels";
  const providers = Array.isArray(data.onlineMallApis?.providers) ? data.onlineMallApis.providers as AnyRecord[] : [];
  const contentRows = contentIntegrationRows(data);
  const posRows = posIntegrationRows();
  const views: SavedView[] = [
    { key: "channels", label: "온라인몰", count: providers.length },
    { key: "pos", label: "매장 POS", count: posRows.length },
    { key: "content", label: "콘텐츠", count: contentRows.length }
  ];

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "channels" ? {} : { view })} />
        <div className="archive-toolbar-copy">
          <strong>외부 서비스 연결 상태와 인증을 관리합니다.</strong>
          <span>Cafe24, POS, Sanity 같은 외부 시스템 연결은 이 화면에서 확인합니다.</span>
        </div>
      </section>
      {activeView === "content" ? <ContentIntegrations rows={contentRows} /> : activeView === "pos" ? <PosIntegrations rows={posRows} /> : <ChannelSettings data={data} drawer={drawer} setDrawer={setDrawer} />}
    </div>
  );
}

function PosIntegrations({ rows }: { rows: AnyRecord[] }) {
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "name",
      header: "POS",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.name}</strong>
          <span>{row.original.key}</span>
        </div>
      ),
      size: 260
    },
    { accessorKey: "purpose", header: "역할", cell: ({ row }) => row.original.purpose },
    { accessorKey: "status", header: "상태", cell: ({ row }) => integrationStatusBadge(row.original.status) },
    { accessorKey: "nextAction", header: "다음 작업", cell: ({ row }) => row.original.nextAction }
  ], []);

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>매장 POS 연결</h2>
          <p>소규모 운영은 Stores의 수동 판매 차감으로 시작하고, POS는 판매 데이터 자동 차감이 필요해질 때 연결합니다.</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.key || row.name)}
        emptyTitle="POS 연결 정보가 없습니다."
      />
    </section>
  );
}

function ContentIntegrations({ rows }: { rows: AnyRecord[] }) {
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "name",
      header: "서비스",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.name}</strong>
          <span>{row.original.key}</span>
        </div>
      ),
      size: 260
    },
    { accessorKey: "purpose", header: "역할", cell: ({ row }) => row.original.purpose },
    { accessorKey: "account", header: "계정/프로젝트", cell: ({ row }) => row.original.account || "-" },
    { accessorKey: "status", header: "상태", cell: ({ row }) => integrationStatusBadge(row.original.status) },
    { accessorKey: "nextAction", header: "다음 작업", cell: ({ row }) => row.original.nextAction }
  ], []);

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>콘텐츠 연결</h2>
          <p>Archive와 콘텐츠 운영에 필요한 외부 서비스 연결 상태입니다.</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.key || row.name)}
        emptyTitle="콘텐츠 연결 정보가 없습니다."
      />
    </section>
  );
}

function contentIntegrationRows(data: AppData): AnyRecord[] {
  const archivePosts = (data as AnyRecord).archivePosts as AnyRecord | undefined;
  return [
    {
      key: "sanity-archive",
      name: "Sanity Archive",
      purpose: "브랜드 기록과 캠페인 콘텐츠 작성",
      account: "83em9rws / production",
      status: "connected",
      nextAction: "Archive 글 큐에서 발행 전 점검"
    },
    {
      key: "email",
      name: "Email sending",
      purpose: "계정 초대와 운영 알림 발송",
      account: "-",
      status: "planned",
      nextAction: "초대 메일 발송 방식 결정"
    },
    {
      key: "archive-json",
      name: "Published JSON",
      purpose: "Cafe24 archive 페이지가 읽는 공개 데이터",
      account: String(archivePosts?.project?.dataset || "production"),
      status: "connected",
      nextAction: "Cafe24 화면 표시 확인"
    }
  ];
}

function posIntegrationRows(): AnyRecord[] {
  return [
    {
      key: "manual-store-sales",
      name: "수동 매장 판매 차감",
      purpose: "POS 없이 바코드 스캔으로 매장 판매 후 재고 차감",
      status: "connected",
      nextAction: "Stores에서 오프라인 판매 차감 사용"
    },
    {
      key: "pos-api",
      name: "POS API",
      purpose: "POS 판매 데이터를 읽어 매장 위치 재고 자동 차감",
      status: "planned",
      nextAction: "사용할 POS 확정 후 계정/키 저장 흐름 추가"
    }
  ];
}

function integrationStatusBadge(status: unknown) {
  const value = String(status || "");
  if (value === "connected") return <StatusBadge tone="success">연결됨</StatusBadge>;
  if (value === "planned") return <StatusBadge tone="warning">준비 중</StatusBadge>;
  return <StatusBadge>확인</StatusBadge>;
}
